/** HomeScreen — ロビー画面。
 *  Reference: docs/CLAUDE_CODE_UI_IMPLEMENTATION_BRIEF.md §4.2
 *  Phase A 仕上げ:
 *    - shared_assets/mockups/canvas_kingdom_competitive_home.png を全画面背景に
 *    - HTML 側は image + 透明クリックレイヤー(.home-hit)のみ
 *    - Stage Select オーバーレイは別 DOM 層で動的駆動を継続
 *    - Booster Pouch: Royal Match 風 .rm-* 統一 UI + BoosterStore 残数管理
 */

import homeHeroUrl from '../../../../shared_assets/mockups/canvas_kingdom_competitive_home.png';
import { BoosterStore, BOOSTER_IDS, type BoosterId } from '../../game/BoosterStore';
import { extractGimmickIcons } from '../../game/StageGimmickIcons';
import { StageLoader } from '../../game/StageLoader';
import { StreakStore, STREAK_REWARDS } from '../../game/StreakStore';
import { DailyQuestStore } from '../../game/DailyQuestStore';
import { DailyChallengeStore } from '../../game/DailyChallengeStore';

export interface StageCardInfo {
  readonly stageId: string;
  readonly title: string;
  readonly targetCoverage: number;
  readonly isCleared: boolean;
  readonly isCurrent: boolean;
  readonly isLocked: boolean;
}

/** プレイ前 Booster Pouch で選択されたアイテム集合(Royal Match / Match Factory 風)。
 *  - brushBoost: 開幕 3 秒、速度 1.5×
 *  - bigStart:   開始陣地 5×5 → 7×7
 *  - extraLife:  失敗時に 1 回追加チャンス
 *  selected[] は HomeScreen 内部で計算済み、main.ts は boosterStore.consume() 後に gameplay.start() に渡す。
 */
export interface BoosterSet {
  readonly brushBoost: boolean;
  readonly bigStart: boolean;
  readonly extraLife: boolean;
  readonly totalCost: number;
}

// Phase A: BoosterStore 残数ありの場合は Color 不要(残数 0 の場合のみ Color 課金が必要だが Phase A は購入のみ)
// BoosterStore 残数 > 0 なら無料で消費、0 なら使えない(ショップで補充)
export const BOOSTER_COSTS = {
  brushBoost: 0,
  bigStart: 0,
  extraLife: 0,
} as const;

export interface HomeScreenOptions {
  readonly onPlay: (boosters: BoosterSet) => void;
  /** Stage select オーバーレイから stage が選ばれた時に呼ばれる */
  readonly onSelectStage?: (stageId: string) => void;
  /** 現在の Color 残高(Pouch で残高不足チェックに使う) */
  readonly getColorBalance?: () => number;
  /** ショップモーダルを開く */
  readonly onOpenShop?: () => void;
  /** BoosterStore インスタンス(残数管理) */
  readonly boosterStore?: BoosterStore;
  /** キャラクター選択画面を開く */
  readonly onOpenCharacter?: () => void;
  /** 完成名画ギャラリーモーダルを開く(muses ボタン経由) */
  readonly onOpenGallery?: () => void;
  /** 名画進捗取得(PaintingFragmentStore から渡す) */
  readonly getPaintingProgress?: () => { current: number; max: number };
  // TODO[D-agent merge]: main.ts で new StreakStore() と new DailyQuestStore() を渡してください
  /** ログインストリーク管理ストア(省略時はストリーク UI 非表示) */
  readonly streakStore?: StreakStore;
  /** デイリークエスト管理ストア(省略時はクエスト UI 非表示) */
  readonly dailyQuestStore?: DailyQuestStore;
  /** Chapter 1 完走チェック(章間ブリッジモーダル用) */
  readonly isChapter1Complete?: () => boolean;
  /**
   * PM 統合TODO:
   * - main.ts で `new DailyChallengeStore(StageLoader.listStageIds())` を作って HomeScreen options に渡す
   * - `onPlayDailyChallenge: (stageId) => { currentStageId = stageId; gameplay.start(stageId, {...}); }`
   * - クリア/失敗時に `dailyChallengeStore.recordAttempt(finalCoverage)` を呼ぶ
   */
  /** デイリーチャレンジ管理ストア(省略時はセクション非表示) */
  readonly dailyChallengeStore?: DailyChallengeStore;
  /** デイリーチャレンジ「プレイ」ボタン押下時コールバック */
  readonly onPlayDailyChallenge?: (stageId: string) => void;
}

export class HomeScreen {
  private readonly root: HTMLDivElement;
  private readonly onPlay: (boosters: BoosterSet) => void;
  private readonly onSelectStage?: (stageId: string) => void;
  private readonly onOpenShop?: () => void;
  private readonly onOpenCharacter?: () => void;
  private readonly onOpenGallery?: () => void;
  private readonly boosterStore: BoosterStore;
  private readonly getPaintingProgress?: () => { current: number; max: number };
  private readonly streakStore?: StreakStore;
  private readonly dailyQuestStore?: DailyQuestStore;
  private readonly dailyChallengeStore?: DailyChallengeStore;
  private readonly onPlayDailyChallenge?: (stageId: string) => void;
  private readonly isChapter1Complete?: () => boolean;
  /** Pouch 選択ステート(toggle 式) */
  private boosterSelected: Set<BoosterId> = new Set();
  /** 現在のステージ情報(Pouch バナー用) */
  private currentStageId = '1-01';
  private currentTargetCoverage = 0.3;

  constructor(root: HTMLDivElement, options: HomeScreenOptions) {
    this.root = root;
    this.onPlay = options.onPlay;
    this.onSelectStage = options.onSelectStage;
    this.onOpenShop = options.onOpenShop;
    this.onOpenCharacter = options.onOpenCharacter;
    this.onOpenGallery = options.onOpenGallery;
    this.boosterStore = options.boosterStore ?? new BoosterStore();
    this.getPaintingProgress = options.getPaintingProgress;
    this.streakStore = options.streakStore;
    this.dailyQuestStore = options.dailyQuestStore;
    this.dailyChallengeStore = options.dailyChallengeStore;
    this.onPlayDailyChallenge = options.onPlayDailyChallenge;
    this.isChapter1Complete = options.isChapter1Complete;
    this.render();
  }

  private render(): void {
    this.root.innerHTML = `
      <div class="home-hero">
        <div class="home-hero-frame">
          <img class="home-hero-img" src="${homeHeroUrl}" alt="" draggable="false" />

          <button class="home-hit home-hit--gear" type="button" data-action="gear" aria-label="Settings"></button>

          <button class="home-hit home-hit--shop" type="button" data-action="shop" aria-label="Shop"></button>
          <button class="home-hit home-hit--muses" type="button" data-action="muses" aria-label="Muses"></button>
          <button class="home-hit home-hit--events" type="button" data-action="events" aria-label="Events"></button>

          <button class="home-hit home-hit--rankings" type="button" data-action="rankings" aria-label="Rankings"></button>
          <button class="home-hit home-hit--quests" type="button" data-action="quests" aria-label="Quests"></button>
          <button class="home-hit home-hit--inbox" type="button" data-action="inbox" aria-label="Inbox"></button>

          <button class="home-hit home-hit--mode" type="button" data-action="open-stages" aria-label="Canvas Battle - select stage"></button>
          <button class="home-hit home-hit--play" type="button" data-action="play" aria-label="Play"></button>

          <button class="home-hit home-hit--nav-home" type="button" data-action="nav-home" aria-label="Home"></button>
          <button class="home-hit home-hit--nav-map" type="button" data-action="open-stages" aria-label="Map"></button>
          <button class="home-hit home-hit--nav-brush" type="button" data-action="nav-brush" aria-label="Brush"></button>
          <button class="home-hit home-hit--nav-clan" type="button" data-action="nav-clan" aria-label="Clan"></button>
        </div>
      </div>

      <div class="stage-select" data-stage-select aria-hidden="true">
        <div class="stage-select-card">
          <div class="stage-select-head">
            <div class="stage-select-title">Stage Map</div>
            <button class="stage-select-close" type="button" data-action="close-stages" aria-label="Close">✕</button>
          </div>
          <div class="stage-select-list" data-stage-list></div>
        </div>
      </div>

      <!-- Royal Match 風 Booster Pouch -->
      <div class="rm-pouch" data-booster-pouch aria-hidden="true">
        <div class="rm-pouch-card rm-card">
          <button class="rm-close-btn" type="button" data-action="close-pouch" aria-label="閉じる">✕</button>
          <div class="rm-pouch-banner-wrap">
            <div class="rm-banner" data-pouch-stage-banner>ステージ 1-01</div>
          </div>
          <div class="rm-pouch-goal">
            <div class="rm-pouch-goal-label">目標</div>
            <div class="rm-pouch-goal-display">
              <span class="rm-pouch-goal-icon">🌼</span>
              <span class="rm-pouch-goal-value" data-pouch-goal>30%</span>
            </div>
          </div>
          <div class="rm-pouch-boosters">
            <div class="rm-pouch-boosters-label">ブースターを選択:</div>
            <div class="rm-pouch-boosters-grid">
              <button class="rm-booster-square" type="button" data-booster="brushBoost" data-count="0">
                <div class="rm-booster-icon">⚡</div>
                <div class="rm-booster-square-label">BRUSH</div>
                <div class="rm-count-badge" data-booster-count="brushBoost">0</div>
              </button>
              <button class="rm-booster-square" type="button" data-booster="bigStart" data-count="0">
                <div class="rm-booster-icon">🟧</div>
                <div class="rm-booster-square-label">BIG START</div>
                <div class="rm-count-badge" data-booster-count="bigStart">0</div>
              </button>
              <button class="rm-booster-square" type="button" data-booster="extraLife" data-count="0">
                <div class="rm-booster-icon">❤️</div>
                <div class="rm-booster-square-label">EXTRA LIFE</div>
                <div class="rm-count-badge" data-booster-count="extraLife">0</div>
              </button>
            </div>
          </div>
          <div class="rm-pouch-actions">
            <button class="rm-cta-primary" type="button" data-action="pouch-play">プレイ</button>
          </div>
        </div>
      </div>

      <div class="home-toast" data-toast hidden></div>

      <!-- 名画進捗バー(第 1 章) -->
      <div class="painting-progress" data-painting-progress>
        <div class="painting-progress-label">第 1 章の名画</div>
        <div class="painting-progress-bar">
          <div class="painting-progress-fill" data-painting-fill style="width:0%"></div>
        </div>
        <div class="painting-progress-count" data-painting-count>0 / 100</div>
      </div>

      <!-- R1/R2: クエスト+ストリーク モーダル(Quests サイドボタンで開閉) -->
      <div class="quests-panel" data-quests-panel aria-hidden="true">
        <div class="quests-panel-card">
          <button class="quests-panel-close" type="button" data-action="close-quests" aria-label="閉じる">✕</button>
          <div class="quests-panel-title">今日のミッション</div>

          <!-- R2: ログインストリーク -->
          <div class="streak-section" data-streak-section>
            <div class="streak-section-title">ログインストリーク</div>
            <div class="streak-days" data-streak-days></div>
            <div class="streak-timer" data-streak-timer></div>
            <button class="streak-claim-btn" type="button" data-action="streak-claim">受け取る</button>
          </div>

          <!-- R1: 今日のクエスト -->
          <div class="quest-section" data-quest-section>
            <div class="quest-section-title">今日のクエスト</div>
            <div class="quest-list" data-quest-list></div>
          </div>

          <!-- F: 今日のチャレンジ -->
          <div class="daily-challenge-section" data-daily-challenge-section>
            <div class="daily-challenge-title" style="font-weight:bold;margin-top:12px;margin-bottom:4px;">今日のチャレンジ</div>
            <div class="daily-challenge-stage" data-daily-challenge-stage style="font-size:0.85em;color:#888;">—</div>
            <div class="daily-challenge-best" style="font-size:0.85em;margin-top:2px;">自己ベスト: <strong data-daily-challenge-best>—</strong></div>
            <button class="daily-challenge-btn" type="button" data-action="daily-challenge-play" style="margin-top:8px;padding:6px 18px;border-radius:8px;border:none;background:#c8823a;color:#fff;font-weight:bold;cursor:pointer;">プレイ</button>
          </div>
        </div>
      </div>

      <!-- R3: 章間ブリッジモーダル -->
      <div class="chapter-bridge-modal" data-chapter-bridge aria-hidden="true">
        <div class="chapter-bridge-card">
          <button class="chapter-bridge-close" type="button" data-action="close-bridge" aria-label="閉じる">✕</button>
          <div class="chapter-bridge-title">美術館に新しい翼が！</div>
          <div class="chapter-bridge-sub">第 2 章「Impressionist Gardens」がもうすぐ開幕</div>
          <div class="chapter-silhouette-grid" data-silhouette-grid>
            ${[1,2,3,4,5].map(i => `<div class="chapter-silhouette" aria-label="名画シルエット ${i}"></div>`).join('')}
          </div>
          <button class="chapter-bridge-cta" type="button" data-action="close-bridge">楽しみにする！</button>
        </div>
      </div>
    `;

    // PLAY → Booster Pouch を開く
    this.root
      .querySelectorAll<HTMLButtonElement>('[data-action="play"]')
      .forEach((el) => el.addEventListener('click', () => this.openBoosterPouch()));

    // Pouch 開閉
    this.root
      .querySelector<HTMLButtonElement>('[data-action="close-pouch"]')
      ?.addEventListener('click', () => this.setBoosterPouchOpen(false));
    this.root
      .querySelector<HTMLElement>('[data-booster-pouch]')
      ?.addEventListener('click', (e) => {
        if (e.target === e.currentTarget) this.setBoosterPouchOpen(false);
      });

    // ブースタースクエアのタップ
    this.root.querySelectorAll<HTMLButtonElement>('[data-booster]').forEach((btn) => {
      btn.addEventListener('click', () => {
        const id = btn.dataset.booster as BoosterId | undefined;
        if (!id || !BOOSTER_IDS.includes(id)) return;
        const count = this.boosterStore.count(id);
        if (count <= 0) {
          this.showToast('ショップで入手できます');
          return;
        }
        if (this.boosterSelected.has(id)) {
          this.boosterSelected.delete(id);
          btn.classList.remove('is-selected');
        } else {
          this.boosterSelected.add(id);
          btn.classList.add('is-selected');
        }
      });
    });

    // プレイボタン
    this.root
      .querySelector<HTMLButtonElement>('[data-action="pouch-play"]')
      ?.addEventListener('click', () => this.confirmAndStart());

    // Toast
    const showToast = (msg: string): void => this.showToast(msg);

    // Shop ボタン
    this.root
      .querySelectorAll<HTMLButtonElement>('[data-action="shop"]')
      .forEach((el) => {
        el.addEventListener('click', () => {
          if (this.onOpenShop) {
            this.onOpenShop();
          } else {
            showToast('Shop — Coming soon');
          }
        });
      });

    // Stub buttons
    // nav-brush → キャラクター選択(onOpenCharacter が渡された場合は実機能、未設定は stub)
    this.root
      .querySelectorAll<HTMLButtonElement>('[data-action="nav-brush"]')
      .forEach((el) => {
        el.addEventListener('click', () => {
          if (this.onOpenCharacter) {
            this.onOpenCharacter();
          } else {
            showToast('Brush — Coming soon');
          }
        });
      });

    // 注: 'quests' / 'muses' は実機能が実装済みのため stub から除外。
    // ここに残すと「Coming soon トースト」と「実機能」が二重発火する。
    const stubMessages: Record<string, string> = {
      gear: 'Settings — Coming soon',
      events: 'Events — Coming soon',
      rankings: 'Rankings — Coming soon',
      inbox: 'Inbox — Coming soon',
      'nav-home': 'You are here',
      'nav-clan': 'Clan — Coming soon',
    };
    for (const [action, msg] of Object.entries(stubMessages)) {
      this.root
        .querySelectorAll<HTMLButtonElement>(`[data-action="${action}"]`)
        .forEach((el) => el.addEventListener('click', () => showToast(msg)));
    }
    // Muses ボタン → 完成名画ギャラリーモーダル(onOpenGallery 未注入時のみ Coming soon)
    this.root
      .querySelectorAll<HTMLButtonElement>('[data-action="muses"]')
      .forEach((el) => el.addEventListener('click', () => {
        if (this.onOpenGallery) {
          this.onOpenGallery();
        } else {
          showToast('Gallery — Coming soon');
        }
      }));

    // Stage Select オーバーレイ開閉
    this.root
      .querySelectorAll<HTMLElement>('[data-action="open-stages"]')
      .forEach((el) => {
        el.addEventListener('click', () => this.setStageOverlayOpen(true));
        el.addEventListener('keydown', (e) => {
          if (e instanceof KeyboardEvent && (e.key === 'Enter' || e.key === ' ')) {
            e.preventDefault();
            this.setStageOverlayOpen(true);
          }
        });
      });
    this.root
      .querySelector<HTMLButtonElement>('[data-action="close-stages"]')
      ?.addEventListener('click', () => this.setStageOverlayOpen(false));
    this.root
      .querySelector<HTMLElement>('[data-stage-select]')
      ?.addEventListener('click', (e) => {
        if (e.target === e.currentTarget) this.setStageOverlayOpen(false);
      });

    // R2: ストリーク UI 初期化
    this.renderStreakSection();

    // R1: クエスト UI 初期化
    this.renderQuestSection();

    // F: デイリーチャレンジ UI 初期化
    this.renderDailyChallenge();

    // Quests サイドボタンでパネルを開閉
    this.root
      .querySelector<HTMLElement>('[data-action="quests"]')
      ?.addEventListener('click', () => this.setQuestsPanelOpen(true));
    // 閉じるボタン + 背景クリックで閉じる
    this.root
      .querySelectorAll<HTMLElement>('[data-action="close-quests"]')
      .forEach((el) => el.addEventListener('click', () => this.setQuestsPanelOpen(false)));
    this.root
      .querySelector<HTMLElement>('[data-quests-panel]')
      ?.addEventListener('click', (e) => {
        if (e.target === e.currentTarget) this.setQuestsPanelOpen(false);
      });

    // F: デイリーチャレンジ「プレイ」ボタン
    this.root
      .querySelector<HTMLButtonElement>('[data-action="daily-challenge-play"]')
      ?.addEventListener('click', () => {
        const today = this.dailyChallengeStore?.getToday();
        if (today && today.stageId) {
          this.onPlayDailyChallenge?.(today.stageId);
        }
      });

    // ストリーク Claim ボタン
    this.root
      .querySelector<HTMLButtonElement>('[data-action="streak-claim"]')
      ?.addEventListener('click', () => this.handleStreakClaim());

    // 章間ブリッジモーダル閉じる
    this.root
      .querySelectorAll<HTMLButtonElement>('[data-action="close-bridge"]')
      .forEach((el) => el.addEventListener('click', () => this.setChapterBridgeOpen(false)));
    this.root
      .querySelector<HTMLElement>('[data-chapter-bridge]')
      ?.addEventListener('click', (e) => {
        if (e.target === e.currentTarget) this.setChapterBridgeOpen(false);
      });

    // R3: Chapter 1 完走チェック → 章間ブリッジ自動表示
    if (this.isChapter1Complete?.()) {
      window.setTimeout(() => this.setChapterBridgeOpen(true), 600);
    }
  }

  // --------------------------------------------------------------------------
  // R2: ログインストリーク
  // --------------------------------------------------------------------------

  private renderStreakSection(): void {
    const section = this.root.querySelector<HTMLElement>('[data-streak-section]');
    if (!section) return;
    if (!this.streakStore) {
      section.hidden = true;
      return;
    }

    const store = this.streakStore;
    const state = store.getState();
    const claimed = store.hasClaimedToday();

    // 7 マス生成
    const daysEl = this.root.querySelector<HTMLElement>('[data-streak-days]');
    if (daysEl) {
      daysEl.innerHTML = STREAK_REWARDS.map((color, i) => {
        const dayNum = i + 1;
        const isCurrent = dayNum === state.currentDay;
        const isPast = dayNum < state.currentDay;
        const isClaimed = isPast || (isCurrent && claimed);
        return `
          <div class="streak-day${isCurrent ? ' is-current' : ''}${isClaimed ? ' is-claimed' : ''}">
            <span class="streak-day-num">Day ${dayNum}</span>
            <span class="streak-day-reward">${dayNum === 7 ? '🎨' : '🎨'}${color}</span>
          </div>
        `;
      }).join('');
    }

    // タイマー / 残り時間
    const timerEl = this.root.querySelector<HTMLElement>('[data-streak-timer]');
    if (timerEl) {
      if (claimed) {
        const now = new Date();
        const nextMs = new Date(now.getFullYear(), now.getMonth(), now.getDate() + 1).getTime() - now.getTime();
        const h = Math.floor(nextMs / 3_600_000);
        const m = Math.floor((nextMs % 3_600_000) / 60_000);
        timerEl.textContent = `次のクレームまで ${h}h ${m}m`;
      } else {
        timerEl.textContent = '今日のボーナスを受け取ろう！';
      }
    }

    // Claim ボタン状態
    const claimBtn = this.root.querySelector<HTMLButtonElement>('[data-action="streak-claim"]');
    if (claimBtn) {
      claimBtn.disabled = claimed;
      claimBtn.textContent = claimed ? '受取済み ✓' : '受け取る';
    }
  }

  private handleStreakClaim(): void {
    if (!this.streakStore) return;
    const result = this.streakStore.claimToday();
    if (result.alreadyClaimed) {
      this.showToast('今日はすでに受け取り済みです');
      return;
    }
    let msg = `+${result.colorGained} Color 獲得！`;
    if (result.fragmentGained > 0) {
      msg += ` 名画フラグメント +${result.fragmentGained}`;
    }
    this.showToast(msg, 2400);
    this.renderStreakSection();
  }

  // --------------------------------------------------------------------------
  // R1: デイリークエスト
  // --------------------------------------------------------------------------

  private renderQuestSection(): void {
    const section = this.root.querySelector<HTMLElement>('[data-quest-section]');
    if (!section) return;
    if (!this.dailyQuestStore) {
      section.hidden = true;
      return;
    }

    const quests = this.dailyQuestStore.getDailyQuests();
    const listEl = this.root.querySelector<HTMLElement>('[data-quest-list]');
    if (!listEl) return;

    listEl.innerHTML = quests.map((q) => {
      const pct = Math.min(100, Math.round((q.progress / q.target) * 100));
      const isComplete = q.progress >= q.target;
      return `
        <div class="quest-row${q.claimed ? ' is-claimed' : ''}">
          <div class="quest-row-label">${DailyQuestStore.getQuestLabel(q)}</div>
          <div class="quest-progress">
            <div class="quest-progress-bar">
              <div class="quest-progress-fill" style="width:${pct}%"></div>
            </div>
            <span class="quest-progress-text">${q.progress}/${q.target}</span>
          </div>
          <button class="quest-claim-btn"
            type="button"
            data-action="quest-claim"
            data-quest-id="${q.id}"
            ${(!isComplete || q.claimed) ? 'disabled' : ''}>
            ${q.claimed ? '受取済み ✓' : isComplete ? '受け取る' : '+50 Color'}
          </button>
        </div>
      `;
    }).join('');

    // クエスト個別 Claim ボタンのハンドラを再バインド
    listEl.querySelectorAll<HTMLButtonElement>('[data-action="quest-claim"]').forEach((btn) => {
      btn.addEventListener('click', () => {
        const questId = btn.dataset.questId ?? '';
        this.handleQuestClaim(questId);
      });
    });
  }

  private handleQuestClaim(questId: string): void {
    if (!this.dailyQuestStore) return;
    const result = this.dailyQuestStore.claim(questId);
    if (result.error === 'already_claimed') {
      this.showToast('すでに受け取り済みです');
      return;
    }
    if (result.error === 'not_complete') {
      this.showToast('まだ達成していません');
      return;
    }
    let msg = `+${result.colorGained} Color 獲得！`;
    if (result.boosterGained) {
      msg += ' 全クエスト達成！ブースター +1 🎉';
    }
    this.showToast(msg, 2400);
    this.renderQuestSection();
  }

  // --------------------------------------------------------------------------
  // F: デイリーチャレンジ
  // --------------------------------------------------------------------------

  private renderDailyChallenge(): void {
    const section = this.root.querySelector<HTMLElement>('[data-daily-challenge-section]');
    if (!section) return;
    if (!this.dailyChallengeStore) {
      section.hidden = true;
      return;
    }

    const today = this.dailyChallengeStore.getToday();

    const stageEl = this.root.querySelector<HTMLElement>('[data-daily-challenge-stage]');
    if (stageEl) stageEl.textContent = today.stageId ? `ステージ ${today.stageId}` : '—';

    const bestEl = this.root.querySelector<HTMLElement>('[data-daily-challenge-best]');
    if (bestEl) {
      bestEl.textContent = today.attempts > 0
        ? `${Math.round(today.bestCoverage * 100)}%`
        : '—';
    }
  }

  // --------------------------------------------------------------------------
  // R3: 章間ブリッジモーダル
  // --------------------------------------------------------------------------

  private setQuestsPanelOpen(open: boolean): void {
    const panel = this.root.querySelector<HTMLElement>('[data-quests-panel]');
    if (!panel) return;
    panel.classList.toggle('is-open', open);
    panel.setAttribute('aria-hidden', String(!open));
    if (open) {
      // 開いた瞬間に最新状態を反映
      this.renderStreakSection();
      this.renderQuestSection();
      this.renderDailyChallenge();
    }
  }

  private setChapterBridgeOpen(open: boolean): void {
    const modal = this.root.querySelector<HTMLElement>('[data-chapter-bridge]');
    if (!modal) return;
    modal.setAttribute('aria-hidden', String(!open));
    modal.classList.toggle('is-open', open);
  }

  private setStageOverlayOpen(open: boolean): void {
    const overlay = this.root.querySelector<HTMLElement>('[data-stage-select]');
    if (!overlay) return;
    overlay.setAttribute('aria-hidden', String(!open));
    overlay.classList.toggle('is-open', open);
  }

  /** Booster Pouch の表示。PLAY 押下時に呼ばれ、残数バッジと選択状態を更新してから開く。 */
  private openBoosterPouch(): void {
    this.boosterSelected.clear();
    this.refreshBoosterBadges();
    this.syncBoosterSquaresVisual();
    // バナーに現在ステージ情報を反映
    const bannerEl = this.root.querySelector<HTMLElement>('[data-pouch-stage-banner]');
    const goalEl = this.root.querySelector<HTMLElement>('[data-pouch-goal]');
    if (bannerEl) bannerEl.textContent = `ステージ ${this.currentStageId}`;
    if (goalEl) goalEl.textContent = `${Math.round(this.currentTargetCoverage * 100)}%`;
    this.setBoosterPouchOpen(true);
  }

  private setBoosterPouchOpen(open: boolean): void {
    const overlay = this.root.querySelector<HTMLElement>('[data-booster-pouch]');
    if (!overlay) return;
    overlay.setAttribute('aria-hidden', String(!open));
    overlay.classList.toggle('is-open', open);
  }

  /** 全ブースタースクエアの残数バッジと data-count 属性を更新 */
  private refreshBoosterBadges(): void {
    for (const id of BOOSTER_IDS) {
      const count = this.boosterStore.count(id);
      const badge = this.root.querySelector<HTMLElement>(`[data-booster-count="${id}"]`);
      const square = this.root.querySelector<HTMLButtonElement>(`[data-booster="${id}"]`);
      if (badge) badge.textContent = String(count);
      if (square) {
        square.dataset.count = String(count);
        square.disabled = false; // always clickable (shows toast if 0)
      }
    }
  }

  /** 選択状態に合わせてスクエアの is-selected を同期 */
  private syncBoosterSquaresVisual(): void {
    this.root.querySelectorAll<HTMLButtonElement>('[data-booster]').forEach((btn) => {
      const id = btn.dataset.booster as BoosterId | undefined;
      if (!id) return;
      btn.classList.toggle('is-selected', this.boosterSelected.has(id));
    });
  }

  /** 選択中のブースターを consume し onPlay を呼ぶ */
  private confirmAndStart(): void {
    // 選択分を消費(残数 0 は選択不可なので consume は必ず成功)
    for (const id of this.boosterSelected) {
      this.boosterStore.consume(id);
    }
    this.setBoosterPouchOpen(false);
    this.onPlay({
      brushBoost: this.boosterSelected.has('brushBoost'),
      bigStart: this.boosterSelected.has('bigStart'),
      extraLife: this.boosterSelected.has('extraLife'),
      totalCost: 0, // 残数方式: Color 消費なし
    });
  }

  // --------------------------------------------------------------------------
  // Public API
  // --------------------------------------------------------------------------

  /** Color 残高 — Pouch バナー不要、互換のため残す(no-op)。 */
  setColorBalance(_value: number): void {
    void _value;
  }

  /** Hearts — Home では表示しない。互換のため残す(no-op)。 */
  setHearts(_count: number, _max: number, _msToNext: number): void {
    void _count; void _max; void _msToNext;
  }

  /** 任意メッセージのトースト表示(Daily Login Bonus 等から呼ばれる)。 */
  showToast(message: string, durationMs: number = 1800): void {
    const toast = this.root.querySelector<HTMLDivElement>('[data-toast]');
    if (!toast) return;
    toast.textContent = message;
    toast.hidden = false;
    toast.classList.add('is-visible');
    window.clearTimeout((toast as unknown as { _t?: number })._t);
    (toast as unknown as { _t?: number })._t = window.setTimeout(() => {
      toast.classList.remove('is-visible');
      window.setTimeout(() => { toast.hidden = true; }, 200);
    }, durationMs);
  }

  /** ステージ情報を保存(Pouch バナー/目標に反映)。 */
  setStageInfo(info: { stageId: string; title: string; targetCoverage: number }): void {
    this.currentStageId = info.stageId;
    this.currentTargetCoverage = info.targetCoverage;
    this.updatePaintingProgress();
  }

  /** 名画進捗バーを最新値に更新する。 */
  private updatePaintingProgress(): void {
    const wrap = this.root.querySelector<HTMLElement>('[data-painting-progress]');
    if (!this.getPaintingProgress) {
      // getter 未提供時はチップ非表示(hero のコイン表示と被らないように)
      wrap?.classList.add('is-empty');
      return;
    }
    const { current, max } = this.getPaintingProgress();
    const fill = this.root.querySelector<HTMLElement>('[data-painting-fill]');
    const count = this.root.querySelector<HTMLElement>('[data-painting-count]');
    if (fill) fill.style.width = `${Math.min(100, (current / max) * 100).toFixed(1)}%`;
    if (count) count.textContent = `${current} / ${max}`;
    // current が 0 ならチップ自体を非表示にして coin と被らないよう退避
    if (wrap) wrap.classList.toggle('is-empty', current <= 0);
  }

  /** Stage Select オーバーレイの一覧(動的、main.ts から push される)。
   *  ★ エリア式レイアウト:5 ステージずつ「エリアカード」に束ねる。
   *  40 ステージ → 8 エリアカード。各エリア内に 5 つのミニチップ。 */
  setStages(stages: readonly StageCardInfo[]): void {
    const list = this.root.querySelector<HTMLElement>('[data-stage-list]');
    if (!list) return;
    list.innerHTML = '';
    list.style.minHeight = 'auto';

    // 5 個ずつエリアに分割
    const STAGES_PER_AREA = 5;
    const areas: { idx: number; meta: AreaMeta; stages: StageCardInfo[] }[] = [];
    for (let i = 0; i < stages.length; i += STAGES_PER_AREA) {
      const chunk = stages.slice(i, i + STAGES_PER_AREA);
      const idx = Math.floor(i / STAGES_PER_AREA);
      areas.push({ idx, meta: AREA_METAS[idx] ?? defaultAreaMeta(idx + 1), stages: chunk });
    }

    // 章ヘッダー
    const header = document.createElement('div');
    header.className = 'stage-area-chapter-header';
    header.innerHTML = `<span class="stage-area-chapter-label">第 1 章 — Renaissance Atelier</span>`;
    list.appendChild(header);

    let currentCard: HTMLElement | null = null;
    for (const area of areas) {
      const clearedCount = area.stages.filter((s) => s.isCleared).length;
      const hasCurrent = area.stages.some((s) => s.isCurrent);
      const allLocked = area.stages.every((s) => s.isLocked);
      const allCleared = clearedCount === area.stages.length;
      const progressPct = Math.round((clearedCount / area.stages.length) * 100);

      // エリア内ステージから主役ギミック上位 2 個を抽出(重複排除)
      const gimmickSet = new Map<string, { icon: string; label: string; priority: number }>();
      for (const s of area.stages) {
        if (s.isLocked) continue;
        try {
          const stageJson = StageLoader.load(s.stageId);
          const icons = extractGimmickIcons(stageJson, 2);
          for (const g of icons) {
            if (!gimmickSet.has(g.id)) gimmickSet.set(g.id, g);
          }
        } catch {
          /* noop */
        }
      }
      const topGimmicks = Array.from(gimmickSet.values())
        .sort((a, b) => b.priority - a.priority)
        .slice(0, 3);

      const card = document.createElement('section');
      card.className = 'stage-area-card';
      if (hasCurrent) card.classList.add('stage-area-card--current');
      if (allLocked) card.classList.add('stage-area-card--locked');
      if (allCleared) card.classList.add('stage-area-card--cleared');

      const chipsHtml = area.stages.map((s) => this.buildStageChip(s)).join('');
      const gimmickHtml = topGimmicks.length > 0
        ? `<div class="stage-area-gimmicks">${topGimmicks
            .map((g) => `<span class="stage-gimmick-chip" title="${g.label}">${g.icon}</span>`)
            .join('')}</div>`
        : '';
      const firstId = area.stages[0]?.stageId ?? '';
      const lastId = area.stages[area.stages.length - 1]?.stageId ?? '';

      card.innerHTML = `
        <header class="stage-area-head">
          <div class="stage-area-title-row">
            <span class="stage-area-num">${area.idx + 1}</span>
            <div class="stage-area-title-block">
              <div class="stage-area-title">${area.meta.title}</div>
              <div class="stage-area-range">${firstId} 〜 ${lastId}</div>
            </div>
            <div class="stage-area-progress-num">${clearedCount}/${area.stages.length}</div>
          </div>
          <div class="stage-area-progress-bar" aria-hidden="true">
            <div class="stage-area-progress-fill" style="width: ${progressPct}%"></div>
          </div>
        </header>
        <div class="stage-area-chips" data-area-chips></div>
        ${gimmickHtml}
      `;

      // chips を append(innerHTML で組み立てたものを置き換え)
      const chipsContainer = card.querySelector<HTMLElement>('[data-area-chips]');
      if (chipsContainer) chipsContainer.innerHTML = chipsHtml;

      // 各 chip にクリックハンドラ
      for (const btn of card.querySelectorAll<HTMLButtonElement>('[data-stage-id]')) {
        const id = btn.dataset.stageId!;
        if (btn.disabled) continue;
        btn.addEventListener('click', () => {
          this.setStageOverlayOpen(false);
          this.onSelectStage?.(id);
        });
      }

      list.appendChild(card);
      if (hasCurrent) currentCard = card;
    }

    // 章フッター
    const footer = document.createElement('div');
    footer.className = 'stage-area-chapter-footer';
    footer.innerHTML = `<span class="stage-area-chapter-label">— 第 1 章 完 —</span>`;
    list.appendChild(footer);

    // 現在のエリアへ自動スクロール
    if (currentCard) {
      const target = currentCard;
      requestAnimationFrame(() => {
        try {
          target.scrollIntoView({ behavior: 'auto', block: 'center' });
        } catch {
          /* noop */
        }
      });
    }
  }

  /** エリアカード内の 1 ステージ chip ボタン HTML を生成(クリックハンドラは外で wire) */
  private buildStageChip(s: StageCardInfo): string {
    const stateClass = s.isCleared
      ? 'is-cleared'
      : s.isCurrent
        ? 'is-current'
        : s.isLocked
          ? 'is-locked'
          : 'is-unlocked';
    const label = s.isLocked ? '🔒' : s.stageId.split('-')[1] ?? s.stageId;
    const star = s.isCleared ? '★' : s.isCurrent ? '●' : '';
    const titleAttr = s.isLocked
      ? '未解放'
      : `${s.stageId} ${s.title}(目標 ${Math.round(s.targetCoverage * 100)}%)`;
    return `
      <button class="stage-area-chip ${stateClass}" type="button"
              data-stage-id="${s.stageId}"
              title="${titleAttr}"
              aria-label="${titleAttr}"
              ${s.isLocked ? 'disabled' : ''}>
        <span class="stage-area-chip-num">${label}</span>
        ${star ? `<span class="stage-area-chip-star">${star}</span>` : ''}
      </button>
    `;
  }
}

/** エリアメタ情報(8 エリア × 5 ステージ = 40 ステージ用) */
interface AreaMeta {
  readonly title: string;
}
const AREA_METAS: readonly AreaMeta[] = [
  { title: 'はじまりの工房' },     // 1-01..1-05
  { title: '最初の試練' },         // 1-06..1-10
  { title: '工房の混沌' },         // 1-11..1-15
  { title: '影の庭' },             // 1-16..1-20
  { title: '巨匠への道' },         // 1-21..1-25
  { title: 'ワープの間' },         // 1-26..1-30
  { title: '鏡映の館' },           // 1-31..1-35
  { title: '巨匠の最終画布' },     // 1-36..1-40
];
function defaultAreaMeta(n: number): AreaMeta {
  return { title: `エリア ${n}` };
}
